import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  FiSave,
  FiPlus
} from 'react-icons/fi';
import { Zone } from '../../shared/types/zone';
import { zonesApi, validateZoneData, formatZoneForBackend, validateZoneCoordinates } from '../../services/ZonesApiService';
import { useToast } from '../toast/toastProvider';
import ZoneComponent from './Zone';
import ZoneModal from './ZoneModal';
import DrawingControls from './DrawingControls';
import './DrawingViewer.css';

interface DrawingViewerProps {
  drawingUrl: string;
  drawingId: string;
  projectId: string;
  siteName?: string;
  siteLocation?: string;
  onZoneCreate?: (zone: Zone) => void;
  onZoneUpdate?: (zone: Zone) => void;
  onZoneDelete?: (zoneId: string) => void;
  onZonesSave?: (zones: Zone[]) => void;
  className?: string;
  style?: React.CSSProperties;
}

const DrawingViewer: React.FC<DrawingViewerProps> = ({
  drawingUrl,
  drawingId,
  projectId,
  siteName,
  siteLocation,
  onZoneCreate,
  onZoneUpdate,
  onZoneDelete,
  onZonesSave,
  className,
  style
}) => {

  // Drawing state
  const [imageLoaded, setImageLoaded] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Interaction state
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });

  // Zone state
  const [zones, setZones] = useState<Zone[]>([]);
  const [isCreatingZone, setIsCreatingZone] = useState(false);
  const [zonePoints, setZonePoints] = useState<{ x: number; y: number }[]>([]);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [showZoneModal, setShowZoneModal] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [showZones, setShowZones] = useState(true);
  const [pendingZones, setPendingZones] = useState<Partial<Zone>[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Toast hook
  const { addToast } = useToast();

  // Calculate relative coordinates for zone points
  const calculateRelativeCoordinates = useCallback((clientX: number, clientY: number): { x: number; y: number } | null => {
    if (!imageRef.current || !containerRef.current) return null;

    const imageRect = imageRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    // Get the click position relative to the container
    const containerX = clientX - containerRect.left;
    const containerY = clientY - containerRect.top;

    // Get the image position relative to the container (accounting for transforms)
    const imageLeft = imageRect.left - containerRect.left;
    const imageTop = imageRect.top - containerRect.top;

    // Calculate the click position relative to the image
    const imageClickX = containerX - imageLeft;
    const imageClickY = containerY - imageTop;

    // Convert to normalized coordinates (0-1 range) based on the current image size
    const relativeX = imageClickX / imageRect.width;
    const relativeY = imageClickY / imageRect.height;

    // Validate coordinates are within image bounds
    if (relativeX < 0 || relativeX > 1 || relativeY < 0 || relativeY > 1) {
      return null;
    }

    return {
      x: relativeX,
      y: relativeY
    };
  }, []);

  // Handle image load
  const handleImageLoad = useCallback(() => {
    if (imageRef.current && containerRef.current) {
      const naturalWidth = imageRef.current.naturalWidth;
      const naturalHeight = imageRef.current.naturalHeight;
      
      
      // Calculate initial zoom to fit image in container
      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width - 200; // Account for controls
      const containerHeight = containerRect.height - 100; // Account for some padding
      
      const scaleX = containerWidth / naturalWidth;
      const scaleY = containerHeight / naturalHeight;
      const initialZoom = Math.min(scaleX, scaleY, 1); // Don't zoom in beyond 100%
      
      setZoomLevel(initialZoom);
      setImageLoaded(true);
    }
  }, []);

  // Save all pending zones
  const handleSaveZones = useCallback(async () => {
    if (pendingZones.length === 0) {
      addToast({
        type: 'warning',
        title: 'No Zones to Save',
        message: 'Please create at least one zone before saving.',
        duration: 3000
      });
      return;
    }

    setIsSaving(true);
    try {
      const savedZones: Zone[] = [];
      
      for (const zoneData of pendingZones) {
        // Validate zone data
        const validationErrors = validateZoneData({
          ...zoneData,
          drawingId,
          projectId
        });
        
        if (validationErrors.length > 0) {
          addToast({
            type: 'error',
            title: 'Validation Error',
            message: `Zone "${zoneData.name}": ${validationErrors.join(', ')}`,
            duration: 4000
          });
          continue;
        }

        // Validate coordinates
        if (!validateZoneCoordinates(zoneData.coordinates || [])) {
          addToast({
            type: 'error',
            title: 'Invalid Coordinates',
            message: `Zone "${zoneData.name}" has invalid coordinates.`,
            duration: 4000
          });
          continue;
        }

        // Format data for backend
        const formattedData = formatZoneForBackend({
          ...zoneData,
          drawingId,
          projectId
        });

        const result = await zonesApi.createZone(formattedData);
        savedZones.push(result);
        onZoneCreate?.(result);
      }

      // Update zones list
      setZones(prev => [...prev, ...savedZones]);
      setPendingZones([]);
      
      addToast({
        type: 'success',
        title: 'Zones Saved',
        message: `Successfully saved ${savedZones.length} zone(s).`,
        duration: 3000
      });

      // Call the onZonesSave callback
      onZonesSave?.(savedZones);
    } catch (error) {
      console.error('Failed to save zones:', error);
      addToast({
        type: 'error',
        title: 'Failed to Save Zones',
        message: error instanceof Error ? error.message : 'There was an error saving the zones. Please try again.',
        duration: 4000
      });
    } finally {
      setIsSaving(false);
    }
  }, [pendingZones, drawingId, projectId, onZoneCreate, onZonesSave, addToast]);


  // Add zone to pending list
  const handleAddZoneToPending = useCallback(() => {
    if (zonePoints.length < 3) {
      addToast({
        type: 'error',
        title: 'Insufficient Points',
        message: 'Zone must have at least 3 points. Click to add more points.',
        duration: 3000
      });
      return;
    }

    // Validate coordinates
    if (!validateZoneCoordinates(zonePoints)) {
      addToast({
        type: 'error',
        title: 'Invalid Coordinates',
        message: 'Zone coordinates must be valid and within bounds.',
        duration: 4000
      });
      return;
    }

    const newZone: Partial<Zone> = {
      name: `Zone ${zones.length + pendingZones.length + 1}`,
      description: '',
      color: '#3b82f6',
      coordinates: zonePoints,
      drawingId,
      projectId,
      isActive: true,
      tags: []
    };

    setPendingZones(prev => [...prev, newZone]);
    setZonePoints([]);
    setIsCreatingZone(false);
    
    // Automatically open the zone modal for editing
    setEditingZone(newZone as Zone);
    setShowZoneModal(true);
    
    addToast({
      type: 'success',
      title: 'Zone Added',
      message: 'Zone has been added to pending list. Please fill in the zone details.',
      duration: 3000
    });
  }, [zonePoints, zones.length, pendingZones.length, drawingId, projectId, addToast]);

  // Complete zone creation
  const handleCompleteZoneCreation = useCallback(() => {
    handleAddZoneToPending();
  }, [handleAddZoneToPending]);

  // Handle zone point placement
  const handleImageClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!isCreatingZone) return;

    const coordinates = calculateRelativeCoordinates(event.clientX, event.clientY);
    if (!coordinates) {
      console.warn('Click outside image bounds');
      return;
    }

    // Polygon mode - add points
    const newPoint = { x: coordinates.x, y: coordinates.y };
    const updatedPoints = [...zonePoints, newPoint];
    setZonePoints(updatedPoints);
  }, [isCreatingZone, calculateRelativeCoordinates, zonePoints]);

  // Handle pan start
  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    if (event.button !== 0 || isCreatingZone) return; // Only left mouse button, not when creating zones

    setIsPanning(true);
    setLastPanPoint({ x: event.clientX, y: event.clientY });
    event.preventDefault();
  }, [isCreatingZone]);

  // Handle pan move
  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    if (!isPanning) return;

    const deltaX = event.clientX - lastPanPoint.x;
    const deltaY = event.clientY - lastPanPoint.y;

    setPanPosition(prev => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY
    }));

    setLastPanPoint({ x: event.clientX, y: event.clientY });
    event.preventDefault();
  }, [isPanning, lastPanPoint]);

  // Handle pan end
  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev * 1.2, 5));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev / 1.2, 0.1));
  }, []);

  const handleResetView = useCallback(() => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
    setRotation(0);
  }, []);

  const handleRotate = useCallback(() => {
    setRotation(prev => (prev + 90) % 360);
  }, []);

  // Zone management functions


  const handleUpdateZone = useCallback(async (zoneData: Zone) => {
    try {
      // Validate zone data
      const validationErrors = validateZoneData(zoneData);
      if (validationErrors.length > 0) {
        addToast({
          type: 'error',
          title: 'Validation Error',
          message: validationErrors.join(', '),
          duration: 4000
        });
        return;
      }

      // Format data for backend
      const formattedData = formatZoneForBackend(zoneData);
      
      const result = await zonesApi.updateZone(zoneData.id, formattedData);
      setZones(prev => prev.map(zone => zone.id === zoneData.id ? result : zone));
      onZoneUpdate?.(result);
      setShowZoneModal(false);
      setEditingZone(null);
      setSelectedZoneId(null);
      
      addToast({
        type: 'success',
        title: 'Zone Updated',
        message: 'Zone has been updated successfully.',
        duration: 3000
      });
    } catch (error) {
      console.error('Failed to update zone:', error);
      addToast({
        type: 'error',
        title: 'Failed to Update Zone',
        message: error instanceof Error ? error.message : 'There was an error updating the zone. Please try again.',
        duration: 4000
      });
    }
  }, [onZoneUpdate, addToast]);

  const handleDeleteZone = useCallback(async (zoneId: string) => {
    try {
      await zonesApi.deleteZone(zoneId);
      setZones(prev => prev.filter(zone => zone.id !== zoneId));
      onZoneDelete?.(zoneId);
      setSelectedZoneId(null);
      
      addToast({
        type: 'success',
        title: 'Zone Deleted',
        message: 'Zone has been deleted successfully.',
        duration: 3000
      });
    } catch (error) {
      console.error('Failed to delete zone:', error);
      addToast({
        type: 'error',
        title: 'Failed to Delete Zone',
        message: error instanceof Error ? error.message : 'There was an error deleting the zone. Please try again.',
        duration: 4000
      });
    }
  }, [onZoneDelete, addToast]);

  const handleStartZoneCreation = useCallback(() => {
    setIsCreatingZone(true);
    setZonePoints([]);
  }, []);

  const handleStopZoneCreation = useCallback(() => {
    setIsCreatingZone(false);
    setZonePoints([]);
  }, []);

  const handleToggleZoneVisibility = useCallback(() => {
    setShowZones(prev => !prev);
  }, []);

  const handleEditZones = useCallback(() => {
    // This could open a zone management panel
    console.log('Edit zones clicked');
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (event.key.toLowerCase()) {
        case 'z':
          if (isCreatingZone) {
            handleStopZoneCreation();
          } else {
            handleStartZoneCreation();
          }
          break;
        case '=':
        case '+':
          handleZoomIn();
          break;
        case '-':
          handleZoomOut();
          break;
        case 'r':
          handleResetView();
          break;
        case 'escape':
          setIsCreatingZone(false);
          setZonePoints([]);
          setSelectedZoneId(null);
          break;
        case 'enter':
          if (isCreatingZone && zonePoints.length >= 3) {
            handleCompleteZoneCreation();
          }
          break;
        case 's':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            handleSaveZones();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleZoomIn, handleZoomOut, handleResetView, isCreatingZone, zonePoints, handleStartZoneCreation, handleStopZoneCreation, handleCompleteZoneCreation, handleSaveZones]);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);


  return (
    <div 
      className={`drawing-viewer-container ${isFullscreen ? 'fullscreen' : ''} ${isCreatingZone ? 'creating-zone' : ''} ${className || ''}`}
      ref={containerRef}
      style={style}
    >
      {/* Drawing Controls */}
      <DrawingControls
        zoomLevel={zoomLevel}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onResetView={handleResetView}
        onRotate={handleRotate}
        onToggleFullscreen={toggleFullscreen}
        isFullscreen={isFullscreen}
        onStartZoneCreation={handleStartZoneCreation}
        onStopZoneCreation={handleStopZoneCreation}
        onToggleZoneVisibility={handleToggleZoneVisibility}
        onEditZones={handleEditZones}
        isCreatingZone={isCreatingZone}
        showZones={showZones}
        zoneCount={zones.length}
      />

      {/* Main Drawing Area */}
      <div className="drawing-main-area">
        <div 
          className="drawing-image-container"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{
            cursor: isCreatingZone ? 'crosshair' : isPanning ? 'grabbing' : 'grab'
          }}
        >
          <img
            ref={imageRef}
            src={drawingUrl}
            alt="Floor plan"
            className="drawing-image"
            onLoad={handleImageLoad}
            onClick={handleImageClick}
            style={{
              transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoomLevel}) rotate(${rotation}deg)`,
              transformOrigin: 'center',
              transition: isPanning ? 'none' : 'transform 0.3s ease'
            }}
            draggable={false}
          />

          {/* Zone Creation Points */}
          {imageLoaded && isCreatingZone && zonePoints.map((point, index) => {
            if (!imageRef.current) return null;
            
            const imageRect = imageRef.current.getBoundingClientRect();
            const containerRect = imageRef.current.parentElement?.getBoundingClientRect();
            
            if (!containerRect) return null;
            
            // Get the natural image dimensions
            const naturalWidth = imageRef.current.naturalWidth;
            const naturalHeight = imageRef.current.naturalHeight;
            
            // Get the image's position relative to the container
            const imageOffsetX = imageRect.left - containerRect.left;
            const imageOffsetY = imageRect.top - containerRect.top;
            
            // Convert normalized coordinates to pixel positions, accounting for zoom
            const naturalX = point.x * naturalWidth;
            const naturalY = point.y * naturalHeight;
            
            const x = imageOffsetX + (naturalX * zoomLevel);
            const y = imageOffsetY + (naturalY * zoomLevel);
            
            return (
              <div
                key={index}
                className="zone-creation-point"
                style={{
                  position: 'absolute',
                  left: x - 4,
                  top: y - 4,
                  width: 8,
                  height: 8,
                  backgroundColor: '#10b981',
                  border: '2px solid white',
                  borderRadius: '50%',
                  zIndex: 1000,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                  pointerEvents: 'none'
                }}
              />
            );
          })}

          {/* Zones */}
          {imageLoaded && showZones && zones.map((zone) => (
            <ZoneComponent
              key={zone.id}
              zone={zone}
              isSelected={selectedZoneId === zone.id}
              zoomLevel={zoomLevel}
              panPosition={panPosition}
              rotation={rotation}
              imageRef={imageRef}
              onClick={() => {
                setSelectedZoneId(zone.id);
                addToast({
                  type: 'info',
                  title: 'Zone Selected',
                  message: `Zone: ${zone.name} - Right-click or use the edit button to modify this zone.`,
                  duration: 3000
                });
              }}
              onEdit={() => {
                setEditingZone(zone);
                setShowZoneModal(true);
              }}
              onDelete={() => handleDeleteZone(zone.id)}
              onToggleVisibility={() => {
                // Toggle zone visibility
                const updatedZone = { ...zone, isActive: !zone.isActive };
                handleUpdateZone(updatedZone);
              }}
              isVisible={showZones && zone.isActive}
            />
          ))}

          {/* Pending Zones */}
          {imageLoaded && showZones && pendingZones.map((zone, index) => (
            <ZoneComponent
              key={`pending-${index}`}
              zone={zone as Zone}
              isSelected={false}
              zoomLevel={zoomLevel}
              panPosition={panPosition}
              rotation={rotation}
              imageRef={imageRef}
              onClick={() => {}}
              onEdit={() => {}}
              onDelete={() => {
                setPendingZones(prev => prev.filter((_, i) => i !== index));
              }}
              onToggleVisibility={() => {}}
              isVisible={showZones}
            />
          ))}
        </div>

        {/* Zone Creation Controls */}
        {isCreatingZone && (
          <div className="zone-creation-controls">
            <div className="zone-creation-info">
              <span>Click on the drawing to add zone points ({zonePoints.length} points)</span>
              {zonePoints.length >= 3 && (
                <button
                  className="zone-complete-btn"
                  onClick={handleCompleteZoneCreation}
                >
                  <FiPlus size={16} />
                  Complete Zone
                </button>
              )}
              <button
                className="zone-cancel-btn"
                onClick={handleStopZoneCreation}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Save Button */}
        {pendingZones.length > 0 && (
          <div className="save-zones-controls">
            <div className="save-zones-info">
              <span>{pendingZones.length} zone(s) ready to save</span>
              <button
                className="save-zones-btn"
                onClick={handleSaveZones}
                disabled={isSaving}
              >
                <FiSave size={16} />
                {isSaving ? 'Saving...' : 'Save All Zones'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Zone Modal - Only for editing */}
      {showZoneModal && editingZone && (
        <ZoneModal
          zone={editingZone}
          onSave={(zone) => {
            // Check if this is a pending zone
            const isPendingZone = pendingZones.some(pendingZone => 
              pendingZone === editingZone || 
              (pendingZone.name === editingZone.name && pendingZone.coordinates === editingZone.coordinates)
            );
            
            if (isPendingZone) {
              // Update the pending zone
              setPendingZones(prev => prev.map(pendingZone => 
                pendingZone === editingZone ? { ...zone } : pendingZone
              ));
              addToast({
                type: 'success',
                title: 'Zone Updated',
                message: 'Zone details have been updated. Save all zones when ready.',
                duration: 3000
              });
            } else {
              // Update existing zone
              handleUpdateZone(zone as Zone);
            }
            
            setShowZoneModal(false);
            setEditingZone(null);
          }}
          onCancel={() => {
            setShowZoneModal(false);
            setEditingZone(null);
          }}
          loading={false}
          drawingId={drawingId}
          projectId={projectId}
        />
      )}

      {/* Loading Overlay */}
      {!imageLoaded && (
        <div className="drawing-loading-overlay">
          <div className="loading-spinner"></div>
          <span>Loading drawing...</span>
        </div>
      )}

    </div>
  );
};

export default DrawingViewer;